<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import {
    listApproverGrants, grantErrorMessage,
    listSiteAdditions, approveSiteAddition, rejectSiteAddition,
} from '@/api/approver';
import type { ApproverGrant, GrantStatus, SiteAddition } from '@/api/types';
import { formatRelative, formatAbsolute, formatDate } from '@/lib/datetime';

const status = ref<GrantStatus>('pending');
const grants = ref<ApproverGrant[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

// Flow 4: pending site additions for the approver's groups.
const additions = ref<SiteAddition[]>([]);
const additionBusy = ref<string | null>(null);
const additionError = ref<string | null>(null);

async function load() {
    loading.value = true;
    error.value = null;
    try {
        grants.value = await listApproverGrants(status.value);
    } catch (e) {
        error.value = grantErrorMessage(e);
    } finally {
        loading.value = false;
    }
}

async function loadAdditions() {
    try {
        additions.value = await listSiteAdditions();
    } catch (e) {
        additionError.value = grantErrorMessage(e);
    }
}

async function actOnAddition(id: string, fn: () => Promise<unknown>) {
    additionBusy.value = id;
    additionError.value = null;
    try {
        await fn();
        await loadAdditions();
    } catch (e) {
        additionError.value = grantErrorMessage(e);
    } finally {
        additionBusy.value = null;
    }
}

function approveAddition(a: SiteAddition) { actOnAddition(a._id, () => approveSiteAddition(a._id)); }
function rejectAddition(a: SiteAddition) {
    const reason = prompt('Reason for declining (optional):') ?? undefined;
    actOnAddition(a._id, () => rejectSiteAddition(a._id, reason || undefined));
}

watch(status, load);
onMounted(() => { load(); loadAdditions(); });

function expiryLabel(g: ApproverGrant): string {
    return g.expiresAt ? formatDate(g.expiresAt) : '—';
}
</script>

<template>
    <div class="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <h1 class="text-2xl font-semibold mb-1">Access approvals</h1>
        <p class="text-bp-muted text-sm mb-6">
            Review VPN access requests for your group(s). Approving sets an expiry date (max one year);
            access is withdrawn automatically when it lapses.
        </p>

        <div class="flex items-center gap-3 mb-4">
            <label class="text-sm text-bp-muted">Status</label>
            <select v-model="status"
                    class="px-3 py-1.5 bg-bp-bg border border-bp-border rounded text-sm">
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
                <option value="rejected">Rejected</option>
            </select>
        </div>

        <div v-if="error" class="bg-red-900/30 border border-red-700 text-red-200 px-4 py-2 rounded mb-4 text-sm">
            {{ error }}
        </div>

        <div class="bg-bp-panel border border-bp-border rounded">
            <div v-if="loading" class="px-4 py-6 text-sm text-bp-muted">Loading…</div>
            <div v-else-if="grants.length === 0" class="px-4 py-6 text-sm text-bp-muted">
                No {{ status }} requests.
            </div>
            <table v-else class="w-full text-sm">
                <thead class="bg-bp-panel-2 text-bp-muted">
                    <tr>
                        <th class="text-left px-4 py-2 font-medium">Applicant</th>
                        <th class="text-left px-4 py-2 font-medium">Company</th>
                        <th class="text-left px-4 py-2 font-medium">Group</th>
                        <th class="text-left px-4 py-2 font-medium">Requested</th>
                        <th class="text-left px-4 py-2 font-medium">Expires</th>
                        <th class="text-right px-4 py-2 font-medium">&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="g in grants" :key="g._id" class="border-t border-bp-border">
                        <td class="px-4 py-2 text-bp-text">
                            {{ g.user?.displayName || g.user?.email || '—' }}
                            <span class="block text-xs text-bp-muted">{{ g.user?.email }}</span>
                        </td>
                        <td class="px-4 py-2 text-bp-muted">{{ g.user?.company || '—' }}</td>
                        <td class="px-4 py-2 text-bp-muted">{{ g.group?.name || '—' }}</td>
                        <td class="px-4 py-2 text-bp-muted" :title="formatAbsolute(g.createdAt)">
                            {{ formatRelative(g.createdAt) }}
                        </td>
                        <td class="px-4 py-2 text-bp-muted">{{ expiryLabel(g) }}</td>
                        <td class="px-4 py-2 text-right">
                            <RouterLink class="text-brand-light-blue hover:text-bp-link-hover text-xs"
                                        :to="`/approvals/${g._id}`">
                                {{ g.status === 'pending' ? 'Review' : 'Open' }}
                            </RouterLink>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Flow 4: pending site additions awaiting this approver's sign-off. -->
        <template v-if="additions.length">
            <h2 class="text-lg font-semibold mt-8 mb-1">Site additions awaiting approval</h2>
            <p class="text-bp-muted text-sm mb-3">
                Approving adds the site to the group — everyone currently in the group will be able to reach it.
            </p>
            <div v-if="additionError" class="bg-red-900/30 border border-red-700 text-red-200 px-4 py-2 rounded mb-3 text-sm">
                {{ additionError }}
            </div>
            <div class="bg-bp-panel border border-bp-border rounded">
                <table class="w-full text-sm">
                    <thead class="bg-bp-panel-2 text-bp-muted">
                        <tr>
                            <th class="text-left px-4 py-2 font-medium">Site</th>
                            <th class="text-left px-4 py-2 font-medium">Group</th>
                            <th class="text-left px-4 py-2 font-medium">Requested</th>
                            <th class="text-right px-4 py-2 font-medium">&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in additions" :key="a._id" class="border-t border-bp-border">
                            <td class="px-4 py-2 text-bp-text">{{ a.site?.name || a.site?.siteId || a.siteId }}</td>
                            <td class="px-4 py-2 text-bp-muted">{{ a.group?.name || '—' }}</td>
                            <td class="px-4 py-2 text-bp-muted" :title="formatAbsolute(a.createdAt)">{{ formatRelative(a.createdAt) }}</td>
                            <td class="px-4 py-2 text-right whitespace-nowrap">
                                <button type="button" :disabled="additionBusy === a._id"
                                        class="text-bp-muted hover:text-bp-text text-xs mr-3 disabled:opacity-50"
                                        @click="rejectAddition(a)">Decline</button>
                                <button type="button" :disabled="additionBusy === a._id"
                                        class="text-brand-light-blue hover:text-bp-link-hover text-xs disabled:opacity-50"
                                        @click="approveAddition(a)">{{ additionBusy === a._id ? 'Working…' : 'Approve' }}</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>
